import React from 'react'
import Tree, { useTreeState, treeHandlers } from 'react-hyper-tree'
import highlight from 'highlight.js'
import BaseLayout from '../../components/BaseLayout';
import NumberInput from '../../components/NumberInput';
import Checkbox from '../../components/Checkbox';
import Select from '../../components/Select';
import Panel from '../../components/Panel';
import Row from '../../components/Row';
import { smallData, data } from './data'

import './style.scss'
import { classnames } from '../../helpers';

// export interface HyperTreeViewMainProps {
//   classes?: ClassesType;
//   data: TreeNode[];
//   depth?: number;
//   depthGap?: number;
//   disableHorizontalLines?: boolean;
//   disableLines?: boolean;
//   disableVerticalLines?: boolean;
//   displayedName?: string | ((node: any) => string);
//   draggable?: boolean;
//   draggableHandlers: any;
//   gapMode?: 'margin' | 'padding';
//   horizontalLineStyles?: React.CSSProperties;
//   renderDragZone?: (props: DragZoneProps) => React.ReactNode | string | null;
//   renderNode?: (props: DefaultNodeProps) => React.ReactNode | string | null;
//   setOpen?: (node: any) => void;
//   setSelected?: (node: any, selected?: boolean) => void;
//   staticNodeHeight?: number;
//   verticalLineOffset?: number;
//   verticalLineStyles?: React.CSSProperties;
//   verticalLineTopOffset?: number;
// }

const UPDATE_FIELD = 'UPDATE_FIELD'

const updateField = (name, value) => ({
  type: UPDATE_FIELD,
  payload: { name, value }
})

const initialState = {
  gapMode: 'padding',
  disableLines: false,
  disableHorizontalLines: false,
  disableVerticalLines: false,
  depthGap: 20,
  draggable: false,
  verticalLineOffset: 5,
  verticalLineTopOffset: 0,
  hoverColor: 'default',
  selectionColor: 'default',
  multipleSelect: false,
  verticalLineColor: '#c4c4c4',
  horizontalLineColor: '#c4c4c4',
  enhanced: false,
}

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case UPDATE_FIELD: {
      return {
        ...state,
        [action.payload.name]: action.payload.value
      }
    }
    default: return state
  }
}

const gapModeOptions = [
  {
    label: 'Padding',
    value: 'padding',
  },
  {
    label: 'Margin',
    value: 'margin',
  },
]

const colorOptions = [
  {
    label: 'Default',
    value: 'default',
  },
  {
    label: 'White',
    value: 'white',
  },
  {
    label: 'Gray',
    value: 'gray',
  },
]

const hoverOptions = [
  {
    label: 'Default',
    value: 'default',
  },
  {
    label: 'White',
    value: 'white',
  },
  {
    label: 'Gray',
    value: 'gray',
  },
]

const lineColorsOptions = [
  {
    label: 'Black',
    value: 'black',
  },
  {
    label: 'Red',
    value: 'red',
  },
  {
    label: 'Green',
    value: 'green',
  },
  {
    label: 'Blue',
    value: 'blue',
  },
]

const colorMap = {
  default: 'default-color-selected',
  white: 'white-color-selected',
  gray: 'gray-color-selected',
}

const hoverMap = {
  default: 'default-color',
  white: 'white-color',
  gray: 'gray-color',
}

const HyperTree = () => {
  const [state, dispatch] = React.useReducer(reducer, initialState)
  const { required, handlers } = useTreeState(
    {
      id: 'tree',
      data: state.enhanced ? data : smallData,
      defaultOpened: true,
      multipleSelect: state.multipleSelect,
    },
  )

  React.useEffect(() => {
    if (state.enhanced) {
      dispatch(updateField('verticalLineTopOffset', -17))
    } else {
      dispatch(updateField('verticalLineTopOffset', 0))
    }
  }, [state.enhanced])

  // console.log('instance1: ', instance1);
  // React.useEffect(() => {
  //   instance.enhanceNodes()
  //   forceUpdate()
  // }, [enhance, forceUpdate, instance])


  const handleChangeField = React.useCallback(field => value => {
    dispatch(updateField(field, value))
  }, [])

  const renderNodeCode = `
    const renderNode = useCallback(({
      node,
      onToggle,
    }) => (
      <div className="tree-node" key={node.data.title}>
                  <div
                    onClick={onToggle}
                    className={classnames({
                      'tree-icon': true,
                      'empty-icon': !node.hasChildren(),
                      [node.options.opened
                        ? 'close-icon'
                        : 'open-icon']: node.hasChildren(),
                    })}
                  />
                  <div
                    className={classnames({
                      'node-content-wrapper': true,
                      'node-selected': node.isSelected(),
                    })}
                    onClick={
                      () => treeHandlers.trees.tree.handlers.setSelected(
                        node,
                        !node.isSelected(),
                      )
                    }
                  >
                    <div className="titles">
                      <div className="node-title">
                        {node.data.name}
                      </div>
                      {node.data.title && (
                        <div className="node-subtitle">
                          {node.data.title}
                        </div>
                      )}
                    </div>
                    {!!node.options.childrenCount && (
                      <div className="children-length">
                        <div>{node.options.childrenCount}</div>
                      </div>
                    )}
                  </div>
            </div>
    ), [])
  `

  const code = `
  import React${state.enhanced ? ', { useCallback }' : ''} from 'react'
  ${state.enhanced ? `import Tree, {
    useTreeState,
    treeHandlers,
  } from 'react-hyper-tree'` : `import Tree, { useTreeState } from 'react-hyper-tree'`}

  const Component = () => {
    const { required, handlers } = useTreeState({
      id: 'tree',
      data: data,
      defaultOpened: true,
      multipleSelect: ${state.multipleSelect},
    })
    ${state.enhanced ? renderNodeCode : ''}
    return (
      <Tree
                  {...required}
                  {...handlers}
                  horizontalLineStyles={{
                        stroke: '${state.horizontalLineColor}',
                        strokeWidth: 1,
                        strokeDasharray: '1 4',
                  }}
                  verticalLineStyles={{
                        stroke: '${state.verticalLineColor}',
                        strokeWidth: 1,
                        strokeDasharray: '1 4',
                  }}
                  draggable={${state.draggable}}
                  gapMode={${state.gapMode}}
                  depthGap={${state.depthGap}}
                  disableLines={${state.disableLines}}
                  disableHorizontalLines={${state.disableHorizontalLines}}
                  disableVerticalLines={${state.disableVerticalLines}}
                  verticalLineTopOffset={${state.verticalLineTopOffset}}
                  verticalLineOffset={${state.verticalLineOffset}}
                  renderNode={${state.enhanced ? 'renderNode' : 'null'}}
              />
    )
  }

  export default Component`

  const renderIcon = () => (
    <div className="tree-icon" />
  )

  const renderNode = React.useCallback(({ node, onToggle }) => (
    <div className="tree-node" key={node.data.title}>
      <div
        onClick={onToggle}
        className={classnames({
          'tree-icon': true,
          'empty-icon': !node.hasChildren(),
          [node.options.opened ? 'close-icon' : 'open-icon']: node.hasChildren(),
        })}
      />
      <div
        className={classnames({
          'node-content-wrapper': true,
          'node-selected': node.isSelected(),
        })}
        onClick={() => treeHandlers.trees.tree.handlers.setSelected(node, !node.isSelected())}
      >
        <div className="titles">
          <div className="node-title">{node.data.name}</div>
          {node.data.title && <div className="node-subtitle">{node.data.title}</div>}
        </div>
        {!!node.options.childrenCount && (
          <div className="children-length">
            <div>{node.options.childrenCount}</div>
          </div>
        )}
      </div>
    </div>
  ), [])

  return (
    <BaseLayout
      className="example-hyper-tree-wrapper"
      renderIcon={renderIcon}
      title="react hyper tree"
      subTitle="playground"
      link="https://github.com/alekseymakhankov/hyper-tree"
    >
      <Panel label="Properties">
        <Row>
          <NumberInput
            id="depth"
            tooltip="Children indentation related to parent"
            label="Depth"
            type="number"
            min={0}
            max={100}
            value={state.depthGap}
            onChange={handleChangeField('depthGap')} />
          <NumberInput label="Vertical line top offset" min={-100} max={100} value={state.verticalLineTopOffset} onChange={handleChangeField('verticalLineTopOffset')} />
          <NumberInput label="Vertical line offset" min={0} max={100} value={state.verticalLineOffset} onChange={handleChangeField('verticalLineOffset')} />
        </Row>
        <Row>
          <Select
            id="depthGap"
            tooltip="Indentation mode"
            label="Gap Mode"
            options={gapModeOptions}
            value={state.gapMode}
            onChange={handleChangeField('gapMode')}
          />
          <Select
            id="nodeSelection"
            tooltip="You can set any color by css"
            label="Selection node color"
            options={colorOptions}
            value={state.selectionColor}
            onChange={handleChangeField('selectionColor')}
          />
          <Select
            id="nodeHover"
            tooltip="You can set any color by css"
            label="Hover node color"
            options={hoverOptions}
            value={state.hoverColor}
            onChange={handleChangeField('hoverColor')}
          />
        </Row>
        <Row>
          <Select
            color
            label="Vertical line color"
            options={lineColorsOptions}
            value={state.verticalLineColor}
            onChange={handleChangeField('verticalLineColor')}
          />
          <Select
            color
            label="Horizontal line color "
            options={lineColorsOptions}
            value={state.horizontalLineColor}
            onChange={handleChangeField('horizontalLineColor')}
          />
        </Row>
        <Row normal>
          <Checkbox label="Disable vertical lines" onChange={handleChangeField('disableVerticalLines')} />
          <Checkbox label="Disable horizontal lines" onChange={handleChangeField('disableHorizontalLines')} />
        </Row>
        <Row normal>
          <Checkbox label="Disable lines" onChange={handleChangeField('disableLines')} />
          <Checkbox label="Multiple select" onChange={handleChangeField('multipleSelect')} />
        </Row>
        <Row normal>
          <Checkbox label="Draggable" onChange={handleChangeField('draggable')} />
          <Checkbox label="Enhance tree" onChange={handleChangeField('enhanced')} />
        </Row>
      </Panel>

      <Row style={{ marginBottom: '20px' }}>
        <div className="code-wrapper">
          <div className="line-numbers">
            {new Array(state.enhanced ? 89 : 40).fill(null).map((_, index) => (
              <div key={index}>{index + 1}</div>
            ))}
          </div>
          <div className="rendered">
            <pre>
              <code
                className="code-view hljs"
                dangerouslySetInnerHTML={{ __html: highlight.highlightAuto(code, ['js']).value }}
              />
            </pre>
          </div>
        </div>
        <Tree
          key={state.enhanced ? 'enhanced': 'tree'}
          {...required}
          {...handlers}
          horizontalLineStyles={{
            stroke: state.horizontalLineColor,
            strokeWidth: 2,
            strokeDasharray: '2 1',
          }}
          verticalLineStyles={{
            stroke: state.verticalLineColor,
            strokeWidth: 2,
            strokeDasharray: '2 1',
          }}
          draggable={state.draggable}
          gapMode={state.gapMode}
          staticNodeHeight={state.enhanced ? 44 : undefined}
          depthGap={parseInt(state.depthGap)}
          disableLines={state.disableLines}
          disableHorizontalLines={state.disableHorizontalLines}
          disableVerticalLines={state.disableVerticalLines}
          verticalLineTopOffset={parseInt(state.verticalLineTopOffset)}
          verticalLineOffset={parseInt(state.verticalLineOffset)}
          classes={{
            selectedNodeWrapper: state.enhanced ? '' : `selected-node ${colorMap[state.selectionColor]}`,
            nodeWrapper: state.enhanced ? '' : `node-wrapper ${hoverMap[state.hoverColor]}`,
            treeWrapper: state.enhanced ? 'tree-wrapper' : 'tree-wrapper simple',
          }}
          renderNode={state.enhanced ? renderNode : undefined}
        />
      </Row>
    </BaseLayout>
  )
}

export default HyperTree
